import DOMPurify from 'dompurify'

/**
 * Wrap DOMPurify with a single project-wide default so every callsite gets the
 * same policy. We accept rich-text (formatting from Quill/TinyMCE-style editors:
 * paragraphs, headings, lists, links, emphasis) and strip anything that can
 * execute JS: <script>, <iframe>, event handlers, javascript: URLs, srcdoc, etc.
 *
 * DOMPurify already blocks those by default; the explicit config below just
 * documents intent and makes future audits easier.
 *
 * Anchors get target=_blank + rel=noopener via the hook -- external content
 * shouldn't be able to reach window.opener.
 */

const ALLOWED_TAGS = [
  'a', 'b', 'blockquote', 'br', 'code', 'div', 'em', 'h1', 'h2', 'h3', 'h4',
  'h5', 'h6', 'i', 'img', 'li', 'ol', 'p', 'pre', 's', 'small', 'span',
  'strong', 'sub', 'sup', 'u', 'ul',
]

const ALLOWED_ATTR = [
  'href', 'src', 'alt', 'title', 'class', 'target', 'rel',
]

// Runs once per module load. Guarded so SSR / non-browser test envs don't blow up.
if (typeof window !== 'undefined') {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node instanceof HTMLAnchorElement) {
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener noreferrer')
    }
  })
}

/**
 * Sanitize an untrusted HTML string. Always pass user-controlled content
 * through this before feeding it to dangerouslySetInnerHTML.
 *
 *   <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(userBio) }} />
 */
export function sanitizeHtml(dirty: string | null | undefined): string {
  if (!dirty) return ''
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  })
}
